import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PayLaterPill } from "./SectionHeading";
import { HERO_VIDEO } from "./homeData";

const STATS = [
  { value: "5.0", label: "Google rating", icon: true },
  { value: "15+", label: "Years experience" },
  { value: "4×", label: "Interest-free with Afterpay & Zip" },
];

const rise = (delay) => ({ initial: { opacity: 0, y: 40 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] } });

export const HeroSection = () => (
  <section className="relative min-h-[100dvh] flex flex-col justify-end overflow-hidden" data-testid="hero-section">
    <video autoPlay muted loop playsInline webkit-playsinline="true" preload="auto" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.45) saturate(1.1)" }}>
      <source src={HERO_VIDEO} type="video/mp4" />
    </video>
    <div className="absolute inset-0 bg-gradient-to-t from-dream-bg via-dream-bg/40 to-dream-bg/20" />
    <div className="absolute inset-0 bg-gradient-to-r from-dream-bg/80 via-transparent to-transparent" />
    <div className="grain-overlay" />

    <div className="relative z-10 max-w-7xl w-full mx-auto px-6 pt-40 pb-8 md:pb-10">
      <motion.p {...rise(0)} className="font-mono text-[10px] sm:text-xs tracking-[0.18em] sm:tracking-[0.3em] uppercase text-dream-violet mb-6 leading-relaxed" data-testid="hero-eyebrow">
        Dream Makers Recording Group <span className="text-white/30">·</span> <span className="whitespace-nowrap">Tullamarine, Melb</span>
      </motion.p>
      <motion.h1 {...rise(0.1)} className="font-display font-semibold text-white tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl max-w-3xl" data-testid="hero-heading">
        Where sound becomes{" "}
        <span className="font-serif italic font-normal tracking-normal text-transparent bg-clip-text bg-gradient-to-r from-dream-violet via-fuchsia-300 to-cyan-200 pr-2">legacy.</span>
      </motion.h1>
      <motion.p {...rise(0.2)} className="text-white/65 text-base md:text-lg max-w-xl mt-6 leading-relaxed">
        A creative home for artists, producers and DJs. From recording and production to mixing, mastering and beyond — bring your vision to life at Dream Makers.
      </motion.p>
      <motion.p {...rise(0.25)} className="font-mono text-[10px] sm:text-xs tracking-[0.2em] uppercase text-white/45 mt-5 flex flex-wrap gap-x-3 gap-y-1" data-testid="hero-services-line">
        {["Recording", "Production", "Mixing & Mastering", "DJ Room", "Photography"].map((s, i) => (
          <span key={s} className="whitespace-nowrap">{i > 0 && <span className="text-dream-violet mr-3">·</span>}{s}</span>
        ))}
      </motion.p>
      <motion.div {...rise(0.3)} className="flex flex-col sm:flex-row sm:items-center gap-4 mt-10">
        <Link to="/booking">
          <Button data-testid="hero-book-btn" className="group bg-white text-black hover:bg-dream-violet hover:text-white rounded-full px-9 py-7 text-lg font-bold shadow-[0_0_40px_rgba(139,92,246,0.35)] transition-colors">
            Book Your Session
            <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Button>
        </Link>
        <a href="#rooms">
          <Button data-testid="hero-services-btn" variant="outline" className="border-white/25 text-white hover:border-white hover:bg-white/10 rounded-full px-9 py-7 text-lg bg-white/[0.03] backdrop-blur-xl">
            Explore the Rooms
          </Button>
        </a>
        <Link to="/junior-dj-academy">
          <Button data-testid="hero-junior-btn" className="bg-lime-300 hover:bg-lime-200 text-black rounded-full px-9 py-7 text-lg font-bold">
            Junior DJ Academy <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-black text-lime-300">10–17</span>
          </Button>
        </Link>
      </motion.div>
      <motion.div {...rise(0.4)} className="mt-8">
        <PayLaterPill testId="hero-pay-later" />
      </motion.div>

      <motion.div {...rise(0.55)} className="mt-8 grid grid-cols-3 max-w-3xl border-t border-white/10" data-testid="hero-stats">
        {STATS.map((s, i) => (
          <div key={s.label} className={`py-5 pr-4 ${i > 0 ? "pl-4 md:pl-6 border-l border-white/10" : ""}`} data-testid={`hero-stat-${i}`}>
            <p className="font-display text-2xl md:text-3xl font-semibold text-white flex items-center gap-1.5">
              {s.value}
              {s.icon && <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />}
            </p>
            <p className="text-white/45 text-xs md:text-sm mt-1">{s.label}</p>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);
